<template>
  <div class="lesson-content">
    <h1>Install Homebrew (brew)</h1>
    <p>Homebrew is a popular package manager for macOS. It simplifies the installation of software and tools essential for development. In this lesson, we'll cover how to install Homebrew and verify the installation.</p>

    <h2>Step 1: Open Terminal</h2>
    <p>Go to Applications → Utilities → Open Terminal.</p>
    <p>Alternatively, quickly open Terminal using Spotlight Search:</p>
    <ul>
      <li>Press Cmd + Space, type Terminal, and hit Enter.</li>
    </ul>

    <h2>Step 2: Install Homebrew</h2>
    <p>Paste the following command into your Terminal, then press Enter:</p>
    <div class="code-block">
      <pre><code>/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"</code></pre>
      <button class="copy-button" @click="copyCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    <p>You will be prompted for your password. Type your macOS user password and press Enter. The installation script will download and install Homebrew.</p>

    <h2>Step 3: Update your PATH (For Apple Silicon Mac with M1/M2 chips)</h2>
    <p>After installing, you might need to add Homebrew to your PATH. First, check which shell you're using by running:</p>
    <div class="code-block">
      <pre><code>echo $SHELL</code></pre>
      <button class="copy-button" @click="copyShellCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>

    <p>If the output contains <code>zsh</code>, use these commands:</p>
    <div class="code-block">
      <pre><code>echo 'eval "$(/opt/homebrew/bin/brew shellenv)"' >> ~/.zprofile</code></pre>
      <button class="copy-button" @click="copyZshCommand1" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>

    <p>If the output contains <code>bash</code>, use these commands instead:</p>
    <div class="code-block">
      <pre><code>echo 'eval "$(/opt/homebrew/bin/brew shellenv)"' >> ~/.bash_profile</code></pre>
      <button class="copy-button" @click="copyBashCommand1" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>

    <p>Finally, run this command to update your current session (works for both shells):</p>
    <div class="code-block">
      <pre><code>eval "$(/opt/homebrew/bin/brew shellenv)"</code></pre>
      <button class="copy-button" @click="copyPathCommand2" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    <p class="note">(If you are using an Intel-based Mac, Homebrew usually installs in /usr/local/bin/, and the script should automatically configure your PATH. You can skip this step.)</p>

    <h2>Step 4: Verify the Installation</h2>
    <p>After installation, verify that Homebrew is correctly installed by typing:</p>
    <div class="code-block">
      <pre><code>brew --version</code></pre>
      <button class="copy-button" @click="copyVersionCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    <p>The terminal should output something similar to:</p>
    <div class="version-display">
      <pre><code class="output">Homebrew 4.2.18</code></pre>
      <KnowledgePoint 
        knowledgeKey="versionImportance" 
        :title="knowledgePoints.versionImportance.title"
      />
    </div>

    <h2>Step 5: Test Homebrew (Optional but recommended)</h2>
    <p>To make sure Homebrew is working correctly, run this simple command to check for updates:</p>
    <div class="code-block">
      <pre><code>brew update</code></pre>
      <button class="copy-button" @click="copyUpdateCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    <p>You should see an output similar to:</p>
    <pre><code class="output">Already up-to-date.</code></pre>
    <p>or an update summary if new updates are available.</p>
  </div>
</template>

<script>
import KnowledgePoint from '../../../components/KnowledgePoint.vue';
import { knowledgePoints } from '../../../content/knowledge';

export default {
  name: 'Chapter1Lesson1',
  components: {
    KnowledgePoint
  },
  data() {
    return {
      knowledgePoints
    };
  },
  methods: {
    async copyToClipboard(text, button) {
      await navigator.clipboard.writeText(text);
      button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      setTimeout(() => {
        button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
      }, 2000);
    },
    copyCommand(event) {
      const command = '/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyShellCommand(event) {
      const command = 'echo $SHELL';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyZshCommand1(event) {
      const command = 'echo \'eval "$(/opt/homebrew/bin/brew shellenv)"\' >> ~/.zprofile';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyBashCommand1(event) {
      const command = 'echo \'eval "$(/opt/homebrew/bin/brew shellenv)"\' >> ~/.bash_profile';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyPathCommand2(event) {
      const command = 'eval "$(/opt/homebrew/bin/brew shellenv)"';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyVersionCommand(event) {
      const command = 'brew --version';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyUpdateCommand(event) {
      const command = 'brew update';
      this.copyToClipboard(command, event.currentTarget);
    }
  }
}
</script>

<style scoped>
.lesson-content {
  color: var(--text);
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

h1 {
  color: var(--primary);
  font-size: 2rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

h2 {
  color: var(--text);
  font-size: 1.5rem;
  margin: 2rem 0 1rem;
  font-weight: 600;
}

p {
  margin-bottom: 1rem;
}

ul, ol {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

li {
  margin-bottom: 0.5rem;
}

pre {
  background: #f8fafc;
  border-radius: 6px;
  padding: 1rem;
  margin: 1rem 0;
  overflow-x: auto;
  border: 1px solid var(--border);
}

code {
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.9rem;
}

p code {
  background: #f8fafc;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.note {
  background-color: #f8fafc;
  border-left: 4px solid var(--primary);
  padding: 1rem;
  margin: 1rem 0;
  font-style: italic;
}

code.output {
  color: #16a34a;  /* Green color for output text */
}

.code-block {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;  /* Added spacing between consecutive code blocks */
}

.code-block pre {
  flex-grow: 1;
  margin-right: 0.5rem;
}

.copy-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--text);
  opacity: 0.7;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-button:hover {
  opacity: 1;
}

.copy-button svg {
  width: 20px;
  height: 20px;
}

.version-display {
  display: flex;
  align-items: center;
}

.version-display pre {
  margin-right: 1rem;
}
</style> 